import { useMemo, useState, type JSX } from 'react';
import { useSearch } from '@tanstack/react-router';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';
import type { PipelineStatus } from '../api/types';
import { useWsStore } from '../stores/ws';
import { StartBuildingPanel } from '../components/StartBuildingPanel';
import { RiskHeatmap } from '../components/RiskHeatmap';
// #112 / #113 (round-6 close-out) — make the agent-level UX surface
// explicit on Command Center.
import { AutonomyLadderSlider } from '../components/AutonomyLadderSlider';
import {
  AgentLifecycleBadge,
  type AgentLifecycleState,
} from '../components/AgentLifecycleBadge';
import { ActivityIndicators } from '../components/ActivityIndicators';

/**
 * Command Center (FEAT-U02 / FEAT-ADD-012).
 *
 * Live pipeline status with three sub-surfaces:
 *   - PhaseRibbon: 12-phase horizontal progress strip.
 *   - LiveLog: tail of the last 50 frames from the `phase-stream` WS channel.
 *   - ControlBar: pause / resume / cancel the pipeline.
 */
export function CommandCenterRoute(): JSX.Element {
  const search = useSearch({ strict: false }) as { pipeline_id?: string };
  const pipelineId = search.pipeline_id ?? 'default';

  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [controlBusy, setControlBusy] = useState(false);

  // N5.5 / W1.1 (v1.1) — project-scoped; aborts in-flight on switch.
  useProjectScopedEffect(
    (signal) => {
      setIsLoading(true);
      setError(null);
      shaktiApi
        .getPipelineStatus(pipelineId, { signal })
        .then((s) => {
          if (!signal.aborted) setStatus(s);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(
            e instanceof ApiError
              ? e.body?.error ?? e.message
              : e instanceof Error
                ? e.message
                : 'Unknown error',
          );
        })
        .finally(() => {
          if (!signal.aborted) setIsLoading(false);
        });
    },
    [pipelineId],
  );

  const onControl = async (action: 'pause' | 'resume' | 'cancel'): Promise<void> => {
    setControlBusy(true);
    try {
      await shaktiApi.controlPipeline(pipelineId, action);
      // Re-fetch fresh status after successful control action.
      const refreshed = await shaktiApi.getPipelineStatus(pipelineId);
      setStatus(refreshed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Control failed');
    } finally {
      setControlBusy(false);
    }
  };

  return (
    <section aria-labelledby="cc-title" className="mx-auto flex max-w-6xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/command-center
        </span>
        <h1 id="cc-title" className="text-2xl font-semibold text-fg-primary">
          Command Center
        </h1>
        <p className="text-sm text-fg-secondary">
          Pipeline <code className="shakti-mono">{pipelineId}</code> — live status, phase ribbon, and control bar.
        </p>
      </header>

      {error ? <ErrorBanner error={error} onRetry={() => window.location.reload()} /> : null}
      {isLoading ? <SkeletonBlock /> : null}

      {status ? (
        <>
          {/* K4.2 (round-5) — risk tiering above the phase ribbon. */}
          <RiskHeatmap status={status} />
          <PhaseRibbon status={status} />
          <ControlBar status={status} busy={controlBusy} onAction={onControl} />
          {/* #112 / #113 (round-6 close-out) — agent surface with live
               autonomy slider + FSM badge + activity indicators. */}
          <AgentsPanel />
          <LiveLog />
        </>
      ) : !isLoading && !error ? (
        // H3.5 (round-4) — when no pipeline is loaded, show the
        // intent-capture panel so a PM/PO can kick a new build
        // without leaving the Command Center.
        <StartBuildingPanel />
      ) : null}
    </section>
  );
}

// ── Components ─────────────────────────────────────────────────────────────

function PhaseRibbon({ status }: { status: PipelineStatus }): JSX.Element {
  return (
    <div className="shakti-card flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
        Phases ({status.current_phase})
      </h2>
      <ol className="flex flex-wrap gap-1" aria-label="Pipeline phases">
        {status.phases.map((p) => (
          <li
            key={p.phase}
            className={`rounded-sm px-2 py-1 text-xs font-mono ${phaseTone(p.status)}`}
            aria-current={p.phase === status.current_phase ? 'step' : undefined}
            title={p.error ?? undefined}
          >
            {p.phase} · {p.status}
          </li>
        ))}
      </ol>
      {status.eta_seconds !== undefined ? (
        <p className="text-xs text-fg-muted">
          ETA: {Math.round(status.eta_seconds / 60)} min
        </p>
      ) : null}
    </div>
  );
}

function phaseTone(s: string): string {
  switch (s) {
    case 'running':
      return 'bg-status-info/15 text-status-info';
    case 'completed':
      return 'bg-status-success/15 text-status-success';
    case 'failed':
      return 'bg-status-error/15 text-status-error';
    case 'awaiting_approval':
      return 'bg-status-warning/15 text-status-warning';
    case 'cancelled':
      return 'bg-bg-raised text-fg-muted';
    default:
      return 'bg-bg-raised text-fg-secondary';
  }
}

function ControlBar({
  status,
  busy,
  onAction,
}: {
  status: PipelineStatus;
  busy: boolean;
  onAction: (a: 'pause' | 'resume' | 'cancel') => void;
}): JSX.Element {
  const isRunning = status.status === 'running';
  const isCancellable = status.status !== 'completed' && status.status !== 'cancelled';
  return (
    <div className="shakti-card flex items-center justify-between gap-4">
      <span className="text-sm text-fg-secondary">
        Status: <strong className="text-fg-primary">{status.status}</strong>
      </span>
      <div className="flex gap-2">
        <ControlButton
          label="Pause"
          disabled={busy || !isRunning}
          onClick={() => onAction('pause')}
        />
        <ControlButton
          label="Resume"
          disabled={busy || status.status !== 'pending'}
          onClick={() => onAction('resume')}
        />
        <ControlButton
          label="Cancel"
          tone="danger"
          disabled={busy || !isCancellable}
          onClick={() => onAction('cancel')}
        />
      </div>
    </div>
  );
}

function ControlButton({
  label,
  onClick,
  disabled,
  tone = 'default',
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}): JSX.Element {
  const base =
    'rounded-sm border px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40';
  const toneClass =
    tone === 'danger'
      ? 'border-status-error/40 text-status-error hover:bg-status-error/10'
      : 'border-border-subtle text-fg-secondary hover:border-border-strong hover:text-fg-primary';
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${toneClass}`}>
      {label}
    </button>
  );
}

function LiveLog(): JSX.Element {
  const tail = useWsStore((s) => s.channels['phase-stream'].tail);
  const recent = useMemo(() => tail.slice(-50).reverse(), [tail]);
  return (
    <div className="shakti-card flex flex-col gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
        Live log ({recent.length}/50)
      </h2>
      <div className="max-h-80 overflow-y-auto rounded-sm bg-bg-raised p-2 font-mono text-xs">
        {recent.length === 0 ? (
          <p className="text-fg-muted">Waiting for phase-stream events…</p>
        ) : (
          recent.map((m, i) => (
            <div key={i} className="whitespace-pre-wrap text-fg-secondary">
              <span className="text-fg-muted">
                {new Date(m.receivedAt).toLocaleTimeString()}{' '}
              </span>
              {JSON.stringify(m.payload)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ErrorBanner({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-center justify-between rounded-sm border border-status-error/40 bg-status-error/10 px-4 py-2 text-sm text-status-error"
    >
      <span>{error}</span>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-sm border border-status-error/50 px-2 py-1 text-xs"
      >
        Retry
      </button>
    </div>
  );
}

function SkeletonBlock(): JSX.Element {
  return (
    <div className="shakti-card animate-pulse">
      <div className="h-4 w-40 rounded-sm bg-bg-raised" />
      <div className="mt-3 h-3 w-full rounded-sm bg-bg-raised" />
      <div className="mt-2 h-3 w-5/6 rounded-sm bg-bg-raised" />
    </div>
  );
}

// #112 / #113 (round-6 close-out) — Agents panel.
//
// Renders the agent surface required by phase-neg1/interaction-design.json:
//   * AutonomyLadderSlider per agent — sets the 5-rung autonomy rung.
//   * AgentLifecycleBadge — live FSM state from /ws/agent-activity.
//   * ActivityIndicators — tokens/sec, ralph iterations, context bar,
//     latency sparkline.
//
// The data source is the agent_activity WS channel managed by
// useWsStore. Each incoming frame updates the per-agent snapshot.
interface AgentSnapshot {
  agent_id: string;
  agent_name: string;
  state: AgentLifecycleState;
  tokens_per_sec: number;
  active_tool: string | null;
  ralph_iteration: number | null;
  ralph_max: number | null;
  context_used: number;
  latency_ms_series: number[];
  autonomy_rung: 1 | 2 | 3 | 4 | 5;
}

function AgentsPanel(): JSX.Element {
  const frames = useWsStore((s) => s.channels['agent-activity']?.tail ?? []);
  // W1.2 (v1.1) — hydrate rungs from the DB so the sliders paint the
  // persisted value on first render, not a hardcoded default. We keep
  // the `rungs` map in React state so post-write updates stick even
  // when the WS channel doesn't echo the rung back.
  const [rungOverrides, setRungOverrides] = useState<Record<string, 1 | 2 | 3 | 4 | 5>>({});
  const [defaultRung, setDefaultRung] = useState<1 | 2 | 3 | 4 | 5>(2);
  useProjectScopedEffect((signal) => {
    shaktiApi
      .listAgentAutonomy({ signal })
      .then((r) => {
        if (signal.aborted) return;
        const normalised: Record<string, 1 | 2 | 3 | 4 | 5> = {};
        for (const [agent, rung] of Object.entries(r.rungs)) {
          if (rung >= 1 && rung <= 5) {
            normalised[agent] = rung as 1 | 2 | 3 | 4 | 5;
          }
        }
        setRungOverrides(normalised);
        const def = r.default_rung;
        if (def >= 1 && def <= 5) setDefaultRung(def as 1 | 2 | 3 | 4 | 5);
      })
      .catch(() => {
        // best-effort; the slider falls back to the frame-derived
        // value (or the hardcoded 2) if the API 500s.
      });
  }, []);

  const agents = useMemo(
    () => aggregateAgentFrames(frames, rungOverrides, defaultRung),
    [frames, rungOverrides, defaultRung],
  );

  async function persistAutonomy(
    agentId: string,
    rung: 1 | 2 | 3 | 4 | 5,
  ): Promise<void> {
    await shaktiApi.setAgentAutonomy(agentId, rung);
    setRungOverrides((prev) => ({ ...prev, [agentId]: rung }));
  }

  if (agents.length === 0) {
    return (
      <section aria-labelledby="agents-heading" className="shakti-card flex flex-col gap-3">
        <h2 id="agents-heading" className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Agents
        </h2>
        <p className="text-sm text-fg-secondary">
          No active agents yet — the panel will populate as the pipeline dispatches work.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="agents-heading" className="shakti-card flex flex-col gap-4">
      <h2 id="agents-heading" className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
        Agents ({agents.length})
      </h2>
      <ul className="flex flex-col divide-y divide-border-subtle">
        {agents.map((a) => (
          <li key={a.agent_id} className="flex flex-col gap-3 py-3">
            <header className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-fg-primary">{a.agent_name}</span>
                <AgentLifecycleBadge state={a.state} />
              </div>
              <span className="shakti-mono text-2xs text-fg-muted">{a.agent_id}</span>
            </header>
            <ActivityIndicators
              tokensPerSec={a.tokens_per_sec}
              activeTool={a.active_tool}
              ralphIteration={a.ralph_iteration}
              ralphMax={a.ralph_max}
              contextWindowUsed={a.context_used}
              latencyMsSeries={a.latency_ms_series}
            />
            <AutonomyLadderSlider
              agentId={a.agent_id}
              initialRung={a.autonomy_rung}
              onPersist={(r) => persistAutonomy(a.agent_id, r)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * W1.3 (v1.1) — fold the agent-activity WS tail into one row per agent.
 *
 * Each WS frame from `/ws/agent-activity` is the server-wrapped shape:
 *   { type: 'event' | 'connected' | 'closed' | 'error',
 *     channel: 'agent-activity',
 *     event_id, timestamp,
 *     payload: { kind: '<EventPayload variant>', data: { ... } } }
 *
 * We unwrap `payload` + `data` and dispatch per `kind`:
 *   AgentSpawned          → register agent with state `queued`
 *   AgentActivityFrame    → merge rich indicator fields (W1.3 primary)
 *   AgentCompleted        → state `complete` + final tokens + cost
 *   AgentFailed           → state `failed`
 *   FeedbackLoopIteration → state `self_correcting` + Ralph counters
 *   other                 → ignored
 *
 * `ws.ts` stores frames as `{ receivedAt, payload }` — we accept both
 * the `WsMessage` shape and raw frames in case the consumer changes.
 */
export function aggregateAgentFrames(
  frames: unknown[],
  rungOverrides: Record<string, 1 | 2 | 3 | 4 | 5> = {},
  defaultRung: 1 | 2 | 3 | 4 | 5 = 2,
): AgentSnapshot[] {
  const byId = new Map<string, AgentSnapshot>();

  const ensure = (agent_id: string, agent_name?: string): AgentSnapshot => {
    const existing = byId.get(agent_id);
    if (existing) {
      if (agent_name && existing.agent_name === agent_id) {
        existing.agent_name = agent_name;
      }
      return existing;
    }
    const fresh: AgentSnapshot = {
      agent_id,
      agent_name: agent_name ?? agent_id,
      state: 'idle',
      tokens_per_sec: 0,
      active_tool: null,
      ralph_iteration: null,
      ralph_max: null,
      context_used: 0,
      latency_ms_series: [],
      autonomy_rung: rungOverrides[agent_id] ?? defaultRung,
    };
    byId.set(agent_id, fresh);
    return fresh;
  };

  for (const raw of frames) {
    if (!raw || typeof raw !== 'object') continue;
    // WsMessage wrapper from stores/ws.ts → drill into `payload`.
    const msg = raw as { payload?: unknown };
    const inner =
      msg.payload && typeof msg.payload === 'object' ? (msg.payload as Record<string, unknown>) : (raw as Record<string, unknown>);
    // Server-wrapped frame `{ type: 'event', payload: { kind, data } }`.
    const framePayload =
      inner.payload && typeof inner.payload === 'object'
        ? (inner.payload as Record<string, unknown>)
        : inner;
    const kind = typeof framePayload.kind === 'string' ? framePayload.kind : undefined;
    const data =
      framePayload.data && typeof framePayload.data === 'object'
        ? (framePayload.data as Record<string, unknown>)
        : framePayload;
    if (!kind) continue;

    const agent_id =
      typeof data.agent_id === 'string'
        ? data.agent_id
        : typeof data.agent_id === 'object' && data.agent_id !== null
          // some typed IDs serialize as { value: "..." } — accept both.
          ? String((data.agent_id as { value?: unknown }).value ?? '')
          : null;
    if (!agent_id) continue;

    switch (kind) {
      case 'AgentSpawned': {
        const snap = ensure(
          agent_id,
          typeof data.agent_name === 'string' ? data.agent_name : undefined,
        );
        snap.state = 'queued';
        break;
      }
      case 'AgentActivityFrame': {
        const snap = ensure(
          agent_id,
          typeof data.agent_name === 'string' ? data.agent_name : undefined,
        );
        if (typeof data.fsm_state === 'string') {
          snap.state = data.fsm_state as AgentLifecycleState;
        }
        if (typeof data.tokens_per_sec === 'number') {
          snap.tokens_per_sec = data.tokens_per_sec;
        }
        if (typeof data.active_tool === 'string') {
          snap.active_tool = data.active_tool;
        }
        if (typeof data.ralph_iteration === 'number') {
          snap.ralph_iteration = data.ralph_iteration;
        }
        if (typeof data.ralph_max === 'number') {
          snap.ralph_max = data.ralph_max;
        }
        if (typeof data.context_used_pct === 'number') {
          snap.context_used = data.context_used_pct;
        }
        if (typeof data.latency_ms === 'number') {
          snap.latency_ms_series = [...snap.latency_ms_series, data.latency_ms].slice(-20);
        }
        if (
          typeof data.autonomy_rung === 'number'
          && data.autonomy_rung >= 1
          && data.autonomy_rung <= 5
        ) {
          snap.autonomy_rung = data.autonomy_rung as 1 | 2 | 3 | 4 | 5;
        } else if (rungOverrides[agent_id] !== undefined) {
          snap.autonomy_rung = rungOverrides[agent_id];
        }
        break;
      }
      case 'AgentCompleted': {
        const snap = ensure(agent_id);
        snap.state = 'complete';
        break;
      }
      case 'AgentFailed': {
        const snap = ensure(agent_id);
        snap.state = 'failed';
        break;
      }
      case 'FeedbackLoopIteration': {
        const snap = ensure(agent_id);
        snap.state = 'self_correcting';
        if (typeof data.iteration === 'number') snap.ralph_iteration = data.iteration;
        if (typeof data.max_iterations === 'number') snap.ralph_max = data.max_iterations;
        break;
      }
      default:
        break;
    }
  }

  // Surface agents that only exist in DB (never emitted a frame) so
  // the operator can still see + adjust their autonomy rung.
  for (const [agent_id, rung] of Object.entries(rungOverrides)) {
    if (byId.has(agent_id)) continue;
    byId.set(agent_id, {
      agent_id,
      agent_name: agent_id,
      state: 'idle',
      tokens_per_sec: 0,
      active_tool: null,
      ralph_iteration: null,
      ralph_max: null,
      context_used: 0,
      latency_ms_series: [],
      autonomy_rung: rung,
    });
  }
  return Array.from(byId.values()).sort((a, b) => a.agent_name.localeCompare(b.agent_name));
}
